import { getTablePermissions } from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";
import { APPLICATION_STATUSES } from "@/types/db/applications";

export const getApplicationTableConfig = (userRoles: UserRoleType[] | null, displayNames?) => {
  const permissions = getTablePermissions(userRoles, "applications");

  return {
    entityName: "Application",
    entityNamePlural: "Applications",

    tableConfig: {
      ignore: ["tenant_id", "meta_data"],
      readonly: ["id", "applicant_name", "email", "phone", "created_at"],
      validators: {},
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.applications?.name ?? "Application"} Id`;
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        created_at: "date" as const,
        status: {
          type: "enum" as const,
          options: APPLICATION_STATUSES.map((status) => ({
            value: status,
            color: getStatusColor(status),
          })),
        },
      },
    },

    filterChoices: [
      {
        name: "id",
        kind: "text" as const,
        displayName: `${displayNames?.applications?.name ?? "Application"} Id`,
        menus: ["filter", "visible"] as const,
      },
      { name: "applicant_name", kind: "text", menus: ["filter", "visible"] as const },
      { name: "email", kind: "text", menus: ["filter", "visible"] as const },
      { name: "phone", kind: "text", menus: ["filter", "visible"] as const },
      { name: "status", kind: "text", menus: ["filter", "visible"] as const },
      { name: "created_at", kind: "date", menus: ["filter", "visible", "orderby"] as const },
    ],

    ...permissions,
    storageKey: "application_flyout",
  };
};

function getStatusColor(status: string): string {
  switch (status) {
    case "DRAFT":
      return "neutral";
    case "SUBMITTED":
      return "todo";
    case "REVIEWING":
      return "pending";
    case "APPROVED":
      return "complete";
    case "REJECTED":
      return "attention";
    case "WITHDRAWN":
      return "neutral";
    default:
      return "neutral";
  }
}
